/**
 * Accessibility Settings Panel
 *
 * A comprehensive settings panel for users to customize accessibility options.
 * Implements WCAG 2.1 AA requirements for:
 * - High contrast modes
 * - Text scaling
 * - Reduced motion
 * - Screen reader optimizations
 * - Keyboard navigation preferences
 */

import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useAccessibility,
  TextScale,
  ContrastMode,
  ColorScheme,
  FocusStyle,
  AccessibilitySettings,
} from '@/contexts/AccessibilityContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Button } from '../ui/button';
import { Separator } from '../ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Slider } from '../ui/slider';

// ============================================================================
// COMPONENT
// ============================================================================

export function AccessibilitySettingsPanel() {
  const { t } = useTranslation();
  const {
    settings,
    updateSettings,
    resetSettings,
    isPanelOpen,
    closeAccessibilityPanel,
    announce,
  } = useAccessibility();

  const handleSettingChange = useCallback(
    <K extends keyof AccessibilitySettings>(key: K, value: AccessibilitySettings[K]) => {
      updateSettings({ [key]: value });
      announce(`${key} updated to ${value}`);
    },
    [updateSettings, announce]
  );

  const handleReset = useCallback(() => {
    resetSettings();
    announce('Accessibility settings reset to defaults');
  }, [resetSettings, announce]);

  const textScaleOptions: { value: TextScale; label: string }[] = [
    { value: '100', label: '100% (Default)' },
    { value: '125', label: '125%' },
    { value: '150', label: '150%' },
    { value: '175', label: '175%' },
    { value: '200', label: '200%' },
  ];

  const contrastOptions: { value: ContrastMode; label: string }[] = [
    { value: 'normal', label: t('accessibility.contrast.normal', 'Normal') },
    { value: 'high', label: t('accessibility.contrast.high', 'High Contrast') },
    { value: 'highest', label: t('accessibility.contrast.highest', 'Maximum Contrast') },
  ];

  const colorSchemeOptions: { value: ColorScheme; label: string }[] = [
    { value: 'system', label: t('accessibility.colorScheme.system', 'System Default') },
    { value: 'light', label: t('accessibility.colorScheme.light', 'Light') },
    { value: 'dark', label: t('accessibility.colorScheme.dark', 'Dark') },
  ];

  const focusStyleOptions: { value: FocusStyle; label: string }[] = [
    { value: 'default', label: t('accessibility.focus.default', 'Default') },
    { value: 'enhanced', label: t('accessibility.focus.enhanced', 'Enhanced') },
    { value: 'high-visibility', label: t('accessibility.focus.highVisibility', 'High Visibility') },
  ];

  return (
    <Dialog open={isPanelOpen} onOpenChange={(open) => !open && closeAccessibilityPanel()}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
        aria-describedby="accessibility-description"
      >
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold">
            {t('accessibility.title', 'Accessibility Settings')}
          </DialogTitle>
          <DialogDescription id="accessibility-description">
            {t(
              'accessibility.description',
              'Customize your experience for better accessibility. Changes are saved automatically.'
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Visual Settings Section */}
          <section aria-labelledby="visual-settings-heading">
            <h3 id="visual-settings-heading" className="text-lg font-medium mb-4">
              {t('accessibility.visual.title', 'Visual Settings')}
            </h3>

            {/* Text Size */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label htmlFor="text-scale" className="flex-1">
                  <span className="font-medium">
                    {t('accessibility.textScale.label', 'Text Size')}
                  </span>
                  <p className="text-sm text-muted-foreground">
                    {t('accessibility.textScale.description', 'Adjust the size of text throughout the application')}
                  </p>
                </Label>
                <Select
                  value={settings.textScale}
                  onValueChange={(value) => handleSettingChange('textScale', value as TextScale)}
                >
                  <SelectTrigger id="text-scale" className="w-36">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {textScaleOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Contrast Mode */}
              <div className="flex items-center justify-between">
                <Label htmlFor="contrast-mode" className="flex-1">
                  <span className="font-medium">
                    {t('accessibility.contrast.label', 'Contrast Mode')}
                  </span>
                  <p className="text-sm text-muted-foreground">
                    {t('accessibility.contrast.description', 'Increase color contrast for better visibility')}
                  </p>
                </Label>
                <Select
                  value={settings.contrastMode}
                  onValueChange={(value) => handleSettingChange('contrastMode', value as ContrastMode)}
                >
                  <SelectTrigger id="contrast-mode" className="w-44">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {contrastOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Color Scheme */}
              <div className="flex items-center justify-between">
                <Label htmlFor="color-scheme" className="flex-1">
                  <span className="font-medium">
                    {t('accessibility.colorScheme.label', 'Color Scheme')}
                  </span>
                  <p className="text-sm text-muted-foreground">
                    {t('accessibility.colorScheme.description', 'Choose light or dark mode')}
                  </p>
                </Label>
                <Select
                  value={settings.colorScheme}
                  onValueChange={(value) => handleSettingChange('colorScheme', value as ColorScheme)}
                >
                  <SelectTrigger id="color-scheme" className="w-44">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {colorSchemeOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Reduce Transparency */}
              <div className="flex items-center justify-between">
                <Label htmlFor="reduce-transparency" className="flex-1">
                  <span className="font-medium">
                    {t('accessibility.reduceTransparency.label', 'Reduce Transparency')}
                  </span>
                  <p className="text-sm text-muted-foreground">
                    {t('accessibility.reduceTransparency.description', 'Reduce visual transparency effects')}
                  </p>
                </Label>
                <Switch
                  id="reduce-transparency"
                  checked={settings.reduceTransparency}
                  onCheckedChange={(checked) => handleSettingChange('reduceTransparency', checked)}
                />
              </div>
            </div>
          </section>

          <Separator />

          {/* Motion Settings Section */}
          <section aria-labelledby="motion-settings-heading">
            <h3 id="motion-settings-heading" className="text-lg font-medium mb-4">
              {t('accessibility.motion.title', 'Motion Settings')}
            </h3>

            <div className="space-y-4">
              {/* Reduce Motion */}
              <div className="flex items-center justify-between">
                <Label htmlFor="reduce-motion" className="flex-1">
                  <span className="font-medium">
                    {t('accessibility.reduceMotion.label', 'Reduce Motion')}
                  </span>
                  <p className="text-sm text-muted-foreground">
                    {t('accessibility.reduceMotion.description', 'Minimize animations and transitions')}
                  </p>
                </Label>
                <Switch
                  id="reduce-motion"
                  checked={settings.reduceMotion}
                  onCheckedChange={(checked) => handleSettingChange('reduceMotion', checked)}
                />
              </div>
            </div>
          </section>

          <Separator />

          {/* Focus & Navigation Section */}
          <section aria-labelledby="focus-settings-heading">
            <h3 id="focus-settings-heading" className="text-lg font-medium mb-4">
              {t('accessibility.focus.title', 'Focus & Navigation')}
            </h3>

            <div className="space-y-4">
              {/* Focus Style */}
              <div className="flex items-center justify-between">
                <Label htmlFor="focus-style" className="flex-1">
                  <span className="font-medium">
                    {t('accessibility.focusStyle.label', 'Focus Indicator Style')}
                  </span>
                  <p className="text-sm text-muted-foreground">
                    {t('accessibility.focusStyle.description', 'Choose how focused elements are highlighted')}
                  </p>
                </Label>
                <Select
                  value={settings.focusStyle}
                  onValueChange={(value) => handleSettingChange('focusStyle', value as FocusStyle)}
                >
                  <SelectTrigger id="focus-style" className="w-44">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {focusStyleOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Show Focus Outline */}
              <div className="flex items-center justify-between">
                <Label htmlFor="show-focus-outline" className="flex-1">
                  <span className="font-medium">
                    {t('accessibility.showFocusOutline.label', 'Always Show Focus Outline')}
                  </span>
                  <p className="text-sm text-muted-foreground">
                    {t('accessibility.showFocusOutline.description', 'Show focus outline even when using mouse')}
                  </p>
                </Label>
                <Switch
                  id="show-focus-outline"
                  checked={settings.showFocusOutline}
                  onCheckedChange={(checked) => handleSettingChange('showFocusOutline', checked)}
                />
              </div>

              {/* Keyboard Shortcuts */}
              <div className="flex items-center justify-between">
                <Label htmlFor="keyboard-shortcuts" className="flex-1">
                  <span className="font-medium">
                    {t('accessibility.keyboardShortcuts.label', 'Enable Keyboard Shortcuts')}
                  </span>
                  <p className="text-sm text-muted-foreground">
                    {t('accessibility.keyboardShortcuts.description', 'Use keyboard shortcuts for common actions')}
                  </p>
                </Label>
                <Switch
                  id="keyboard-shortcuts"
                  checked={settings.keyboardShortcutsEnabled}
                  onCheckedChange={(checked) => handleSettingChange('keyboardShortcutsEnabled', checked)}
                />
              </div>
            </div>
          </section>

          <Separator />

          {/* Screen Reader Section */}
          <section aria-labelledby="screenreader-settings-heading">
            <h3 id="screenreader-settings-heading" className="text-lg font-medium mb-4">
              {t('accessibility.screenReader.title', 'Screen Reader')}
            </h3>

            <div className="space-y-4">
              {/* Announce Page Changes */}
              <div className="flex items-center justify-between">
                <Label htmlFor="announce-pages" className="flex-1">
                  <span className="font-medium">
                    {t('accessibility.announcePages.label', 'Announce Page Changes')}
                  </span>
                  <p className="text-sm text-muted-foreground">
                    {t('accessibility.announcePages.description', 'Announce when navigating to new pages')}
                  </p>
                </Label>
                <Switch
                  id="announce-pages"
                  checked={settings.announcePageChanges}
                  onCheckedChange={(checked) => handleSettingChange('announcePageChanges', checked)}
                />
              </div>

              {/* Verbose Labels */}
              <div className="flex items-center justify-between">
                <Label htmlFor="verbose-labels" className="flex-1">
                  <span className="font-medium">
                    {t('accessibility.verboseLabels.label', 'Verbose Labels')}
                  </span>
                  <p className="text-sm text-muted-foreground">
                    {t('accessibility.verboseLabels.description', 'Add more detailed labels for screen readers')}
                  </p>
                </Label>
                <Switch
                  id="verbose-labels"
                  checked={settings.verboseLabels}
                  onCheckedChange={(checked) => handleSettingChange('verboseLabels', checked)}
                />
              </div>
            </div>
          </section>

          <Separator />

          {/* Reading Aids Section */}
          <section aria-labelledby="reading-aids-heading">
            <h3 id="reading-aids-heading" className="text-lg font-medium mb-4">
              {t('accessibility.readingAids.title', 'Reading Aids')}
            </h3>

            <div className="space-y-4">
              {/* Dyslexia Font */}
              <div className="flex items-center justify-between">
                <Label htmlFor="dyslexia-font" className="flex-1">
                  <span className="font-medium">
                    {t('accessibility.dyslexiaFont.label', 'Dyslexia-Friendly Font')}
                  </span>
                  <p className="text-sm text-muted-foreground">
                    {t('accessibility.dyslexiaFont.description', 'Use OpenDyslexic font for easier reading')}
                  </p>
                </Label>
                <Switch
                  id="dyslexia-font"
                  checked={settings.dyslexiaFriendlyFont}
                  onCheckedChange={(checked) => handleSettingChange('dyslexiaFriendlyFont', checked)}
                />
              </div>

              {/* Line Spacing */}
              <div className="flex items-center justify-between">
                <Label htmlFor="line-spacing" className="flex-1">
                  <span className="font-medium">
                    {t('accessibility.lineSpacing.label', 'Line Spacing')}
                  </span>
                  <p className="text-sm text-muted-foreground">
                    {t('accessibility.lineSpacing.description', 'Adjust space between lines of text')}
                  </p>
                </Label>
                <Select
                  value={settings.lineSpacing}
                  onValueChange={(value) =>
                    handleSettingChange('lineSpacing', value as 'normal' | 'relaxed' | 'loose')
                  }
                >
                  <SelectTrigger id="line-spacing" className="w-36">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="normal">{t('accessibility.spacing.normal', 'Normal')}</SelectItem>
                    <SelectItem value="relaxed">{t('accessibility.spacing.relaxed', 'Relaxed')}</SelectItem>
                    <SelectItem value="loose">{t('accessibility.spacing.loose', 'Loose')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Letter Spacing */}
              <div className="flex items-center justify-between">
                <Label htmlFor="letter-spacing" className="flex-1">
                  <span className="font-medium">
                    {t('accessibility.letterSpacing.label', 'Letter Spacing')}
                  </span>
                  <p className="text-sm text-muted-foreground">
                    {t('accessibility.letterSpacing.description', 'Adjust space between letters')}
                  </p>
                </Label>
                <Select
                  value={settings.letterSpacing}
                  onValueChange={(value) =>
                    handleSettingChange('letterSpacing', value as 'normal' | 'wide' | 'wider')
                  }
                >
                  <SelectTrigger id="letter-spacing" className="w-36">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="normal">{t('accessibility.spacing.normal', 'Normal')}</SelectItem>
                    <SelectItem value="wide">{t('accessibility.spacing.wide', 'Wide')}</SelectItem>
                    <SelectItem value="wider">{t('accessibility.spacing.wider', 'Wider')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Show Link Underlines */}
              <div className="flex items-center justify-between">
                <Label htmlFor="link-underlines" className="flex-1">
                  <span className="font-medium">
                    {t('accessibility.linkUnderlines.label', 'Underline Links')}
                  </span>
                  <p className="text-sm text-muted-foreground">
                    {t('accessibility.linkUnderlines.description', 'Always show underlines on links')}
                  </p>
                </Label>
                <Switch
                  id="link-underlines"
                  checked={settings.showLinkUnderlines}
                  onCheckedChange={(checked) => handleSettingChange('showLinkUnderlines', checked)}
                />
              </div>
            </div>
          </section>

          <Separator />

          {/* Interaction Settings Section */}
          <section aria-labelledby="interaction-settings-heading">
            <h3 id="interaction-settings-heading" className="text-lg font-medium mb-4">
              {t('accessibility.interaction.title', 'Interaction')}
            </h3>

            <div className="space-y-4">
              {/* Target Size */}
              <div className="flex items-center justify-between">
                <Label htmlFor="target-size" className="flex-1">
                  <span className="font-medium">
                    {t('accessibility.targetSize.label', 'Button & Link Size')}
                  </span>
                  <p className="text-sm text-muted-foreground">
                    {t('accessibility.targetSize.description', 'Minimum size for clickable elements')}
                  </p>
                </Label>
                <Select
                  value={settings.targetSize}
                  onValueChange={(value) =>
                    handleSettingChange('targetSize', value as 'normal' | 'large' | 'extra-large')
                  }
                >
                  <SelectTrigger id="target-size" className="w-36">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="normal">{t('accessibility.size.normal', 'Normal')}</SelectItem>
                    <SelectItem value="large">{t('accessibility.size.large', 'Large')}</SelectItem>
                    <SelectItem value="extra-large">{t('accessibility.size.extraLarge', 'Extra Large')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </section>
        </div>

        {/* Actions */}
        <div className="flex justify-between pt-4 border-t">
          <Button
            variant="outline"
            onClick={handleReset}
            aria-label={t('accessibility.reset', 'Reset all settings to defaults')}
          >
            {t('accessibility.resetButton', 'Reset to Defaults')}
          </Button>
          <Button onClick={closeAccessibilityPanel}>
            {t('common.done', 'Done')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// ACCESSIBILITY BUTTON
// ============================================================================

export function AccessibilityButton() {
  const { t } = useTranslation();
  const { openAccessibilityPanel, settings } = useAccessibility();

  const hasCustomSettings =
    settings.textScale !== '100' ||
    settings.contrastMode !== 'normal' ||
    settings.reduceMotion ||
    settings.dyslexiaFriendlyFont;

  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={openAccessibilityPanel}
      aria-label={t('accessibility.openSettings', 'Open accessibility settings')}
      className="relative"
    >
      <AccessibilityIcon className="h-5 w-5" />
      {hasCustomSettings && (
        <span className="absolute top-0 right-0 h-2 w-2 rounded-full bg-primary" />
      )}
      <span className="sr-only">
        {t('accessibility.buttonLabel', 'Accessibility')}
      </span>
    </Button>
  );
}

// ============================================================================
// SKIP LINK COMPONENT
// ============================================================================

export function SkipToContent() {
  const { t } = useTranslation();
  const { skipToContent } = useAccessibility();

  return (
    <a
      href="#main-content"
      onClick={(e) => {
        e.preventDefault();
        skipToContent();
      }}
      className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-primary focus:text-primary-foreground focus:rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
    >
      {t('accessibility.skipToContent', 'Skip to main content')}
    </a>
  );
}

// ============================================================================
// ACCESSIBILITY ICON
// ============================================================================

function AccessibilityIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="10" />
      <circle cx="12" cy="8" r="2" />
      <path d="M12 10v4" />
      <path d="M9 18l3-4 3 4" />
      <path d="M8 14h8" />
    </svg>
  );
}

export default AccessibilitySettingsPanel;
